import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { Card } from '@/shared/ui/card';
import { cn } from '@/shared/lib/cn';
import { REFERRAL_STATUS, REFERRAL_STEPS, REWARD_STATUS, stepsDone } from '../../model/referral-status';
import type { MgmParticipantView, RewardStatus } from '../../model/types';
import { REWARD_ICON } from '../../ui/status-icons';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { AnimatedIcon } from '@/shared/components/animated-icon';

type Item = NonNullable<MgmParticipantView['referrals']>[number];

function RewardChip({ status, amountText, label }: { status: RewardStatus; amountText: string; label: string }) {
  const Icon = REWARD_ICON[status];
  const granted = status === 'granted';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-xs',
        granted ? 'mgm-accent-text font-semibold' : 'text-muted-foreground',
      )}
      title={label}
    >
      <Icon className="size-3.5" />
      {granted ? `+${amountText}` : label}
    </span>
  );
}

function Track({ status }: { status: Item['status'] }) {
  if (status === 'rejected') {
    return <p className="text-xs text-muted-foreground">Esta indicação não é elegível.</p>;
  }
  const done = stepsDone(status);
  return (
    <div className="flex items-center gap-1.5" aria-label={`Etapa ${done} de 3: ${REFERRAL_STATUS[status].label}`}>
      {REFERRAL_STEPS.map((step, i) => (
        <div key={step} className="flex min-w-0 flex-1 flex-col gap-1">
          <div className={cn('h-1.5 rounded-full', i < done ? 'mgm-accent-bg' : 'bg-muted')} />
          <span className={cn('truncate text-[10px]', i < done ? 'font-medium text-foreground' : 'text-muted-foreground')}>
            {REFERRAL_STATUS[step].label}
          </span>
        </div>
      ))}
    </div>
  );
}

/** As indicações do participante, com a trilha Indicada → Cadastrada → Convertida e a recompensa. */
export function ReferralList({ items }: { items: Item[] }) {
  return (
    <Card className="p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Suas indicações</p>
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <span className="mgm-accent-soft grid size-12 place-items-center rounded-2xl">
            <AnimatedIcon icon={Users} motion="float" loop className="mgm-accent-text size-6" />
          </span>
          <p className="font-medium">Ninguém por aqui ainda</p>
          <p className="max-w-[16rem] text-sm text-muted-foreground">
            Mande seu link para os amigos. Quem entrar por ele aparece aqui, com o andamento de cada um.
          </p>
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-border/60">
          {items.map((item, i) => {
            const reward = item.reward ? REWARD_STATUS[item.reward.status] : null;
            return (
              <motion.li
                key={item.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 8) * 0.04 }}
                className="flex gap-3 py-3"
              >
                {/* Mesmo avatar DiceBear do admin; semente = id da indicação (nenhum dado pessoal sai daqui). */}
                <LeadAvatar lead={{ _id: item.id, profile: { name: item.name } }} className="size-10" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    {reward && item.reward ? (
                      <RewardChip status={item.reward.status} amountText={item.reward.amountText} label={reward.label} />
                    ) : null}
                  </div>
                  <Track status={item.status} />
                </div>
              </motion.li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
